/**
 * Thin fetch wrapper for the Gudwin REST API.
 *
 * Responsibilities:
 *  - Attach the CSRF double-submit header on mutating requests (cookie
 *    `csrf_token` → header `X-CSRF-Token`, see backend `api/csrf.py`).
 *  - Send the auth session cookie via `credentials: "include"`.
 *  - Parse JSON, surface `ApiError` on non-2xx with the backend's `detail`.
 *
 * The real type-safety layer (typed paths/params) lands when we wire
 * `openapi-fetch` over the generated `types.ts` in PR 10. For now keep this
 * minimal — every call site sees `Promise<T>` and casts via generics.
 */

const CSRF_COOKIE = "csrf_token";
const CSRF_HEADER = "X-CSRF-Token";
const MUTATING = new Set(["POST", "PUT", "PATCH", "DELETE"]);

export class ApiError extends Error {
  readonly status: number;
  readonly detail: unknown;
  /**
   * Server-side correlation id (`request_id` field of the error body, mirrored
   * in the `x-request-id` response header). Set by `apiFetch` whenever the
   * backend returns it; operators copy it from the `ErrorBoundary` fallback
   * UI to match against structlog events (security task 18 §6.1).
   */
  readonly requestId: string | null;
  /**
   * The full parsed error body. Most callers only need `detail`, but some
   * endpoints return structured siblings (e.g. unit hard-delete returns
   * `{detail, children, members, tasks}`); they read those off `body`.
   */
  readonly body: unknown;

  constructor(
    status: number,
    detail: unknown,
    message: string,
    requestId: string | null = null,
    body: unknown = null,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
    this.requestId = requestId;
    this.body = body;
  }
}

export function readCsrfCookie(cookieString: string = document.cookie): string | null {
  if (!cookieString) return null;
  for (const part of cookieString.split("; ")) {
    const eq = part.indexOf("=");
    if (eq < 0) continue;
    if (part.slice(0, eq) === CSRF_COOKIE) {
      return decodeURIComponent(part.slice(eq + 1));
    }
  }
  return null;
}

export interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  /** Override base URL (defaults to "/api"). Useful for tests. */
  baseUrl?: string;
}

const DEFAULT_BASE = "/api";

export async function apiFetch<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { body, baseUrl = DEFAULT_BASE, headers, method = "GET", ...rest } = opts;
  const url = path.startsWith("http") ? path : `${baseUrl}${path}`;

  const finalHeaders = new Headers(headers);
  if (!finalHeaders.has("Accept")) finalHeaders.set("Accept", "application/json");

  let payload: BodyInit | undefined;
  if (body !== undefined) {
    if (body instanceof FormData || body instanceof Blob || typeof body === "string") {
      payload = body as BodyInit;
    } else {
      payload = JSON.stringify(body);
      if (!finalHeaders.has("Content-Type")) {
        finalHeaders.set("Content-Type", "application/json");
      }
    }
  }

  if (MUTATING.has(method.toUpperCase())) {
    const token = readCsrfCookie();
    if (token) finalHeaders.set(CSRF_HEADER, token);
  }

  const res = await fetch(url, {
    ...rest,
    method,
    headers: finalHeaders,
    body: payload,
    credentials: "include",
  });

  if (res.status === 204) return undefined as T;

  const contentType = res.headers.get("content-type") ?? "";
  const isJson = contentType.includes("application/json");
  const data = isJson ? await res.json() : await res.text();

  if (!res.ok) {
    const detail =
      isJson && typeof data === "object" && data !== null && "detail" in data
        ? (data as { detail: unknown }).detail
        : data;
    const message = `API request failed (${res.status})`;
    // `request_id` lives at the top of the body (see backend `_payload`),
    // and is also mirrored in the `x-request-id` header. Prefer the body
    // value (set by FastAPI handlers); fall back to the header (still set
    // when the body is non-JSON or schema-divergent).
    const bodyRequestId =
      isJson &&
      typeof data === "object" &&
      data !== null &&
      "request_id" in data &&
      typeof (data as { request_id: unknown }).request_id === "string"
        ? ((data as { request_id: string }).request_id as string)
        : null;
    const requestId = bodyRequestId ?? res.headers.get("x-request-id");
    throw new ApiError(res.status, detail, message, requestId, data);
  }

  return data as T;
}

export const api = {
  get: <T = unknown>(path: string, opts?: RequestOptions) =>
    apiFetch<T>(path, { ...opts, method: "GET" }),
  post: <T = unknown>(path: string, body?: unknown, opts?: RequestOptions) =>
    apiFetch<T>(path, { ...opts, method: "POST", body }),
  put: <T = unknown>(path: string, body?: unknown, opts?: RequestOptions) =>
    apiFetch<T>(path, { ...opts, method: "PUT", body }),
  patch: <T = unknown>(path: string, body?: unknown, opts?: RequestOptions) =>
    apiFetch<T>(path, { ...opts, method: "PATCH", body }),
  delete: <T = unknown>(path: string, opts?: RequestOptions) =>
    apiFetch<T>(path, { ...opts, method: "DELETE" }),
};
